import EventEmitter from "./eventEmitter";
import { RequestError } from "./errors";

let __endpoint = null;
let __connect = null;

/**
 * Wrapper for fetch
 */
class Fetch extends EventEmitter {
	request({
		endpoint,
		path,
		method = "GET",
		data,
		query = {},
		headers = {},
		auth = true
	}) {
		if (!__endpoint && !endpoint) {
			throw new Error("Fetcher endpoint is not set nor initialized");
		}
		if (auth && !__connect) {
			throw new Error(
				"Authentication requested but connect is not initialized"
			);
		}

		if (Object.keys(query).length) {
			query = Object.entries(query)
				.map(
					([key, value]) =>
						`${encodeURIComponent(key)}=${encodeURIComponent(
							value
						)}`
				)
				.join("&");
			path =
				path.indexOf("?") > -1
					? `${path}&${query}`
					: `${path}?${query}`;
		}

		// we can overwrite the endpoint if needed!!!
		let url = `${endpoint || __endpoint}${path}`;

		if (
			["POST", "PUT", "PATCH"].indexOf(method) > -1 &&
			!(data instanceof window.FormData)
		) {
			if (!headers["Content-Type"]) {
				headers["Content-Type"] = "application/json; charset=utf-8";
			}

			if (data && data instanceof Object) {
				data = JSON.stringify(data);
			}
		}

		if (auth) {
			const { token } = __connect.getToken();
			headers["Authorization"] = `Bearer ${token}`;
		}

		if (!("Accept" in headers)) {
			headers["Accept"] = "application/json";
		}

		return fetch(url, {
			method,
			body: data,
			headers
		}).then(response => {
			// XXX: we could handle 300 responses now on the same endpoint
			if (response.status < 200 || response.status > 299) {
				return response.text().then(text => {
					// Emit error event to be caught somewhere
					this.emit(response.status, { response, text });

					throw new RequestError(
						"[Fetch] Error requesting resource",
						url,
						response.status,
						text
					);
				});
			}

			const contentType = response.headers.get("Content-type") || "";
			if (contentType.indexOf("application/json") !== 0) {
				return response;
			}

			return response.json().then(json => ({
				response: json,
				headers: response.headers,
				status: response.status
			}));
		});
	}

	setToken(token) {
		this.token = token;
	}

	get(path, { query, headers, auth } = {}) {
		return this.request({
			path,
			query,
			headers,
			auth
		});
	}

	post(path, { query, data, headers, auth } = {}) {
		return this.request({
			method: "POST",
			path,
			data,
			query,
			headers,
			auth
		});
	}

	put(path, { query, data, headers, auth } = {}) {
		return this.request({
			method: "PUT",
			path,
			data,
			query,
			headers,
			auth
		});
	}

	patch(path, { query, data, headers, auth } = {}) {
		return this.request({
			method: "PATCH",
			path,
			data,
			query,
			headers,
			auth
		});
	}

	delete(path, { query, headers, auth } = {}) {
		return this.request({
			method: "DELETE",
			path,
			query,
			headers,
			auth
		});
	}
}

let fetcher = new Fetch();

export function initialize(endpoint, connect) {
	__endpoint = endpoint;
	__connect = connect;
}

export default fetcher;
